import type { SavedBroker } from "@freightsimple/generated-dashboard-openapi-client/models/SavedBroker";
import type { ReactNode } from "react";
import { useRef, useState } from "react";
import type { BrokersModifyResult } from "@/Features/Brokers/Dialogs/BrokersModifyDialog";
import { BrokersModifyDialog } from "@/Features/Brokers/Dialogs/BrokersModifyDialog";

// Declarative replacement for the imperative useBrokersModifyBrokerDialog: callers
// await `show(savedBroker)` mid-flow, so a resolver ref bridges the dialog
// back to a promise. Resolves the edited broker + its savedBrokerId, or
// undefined if the user closes the dialog.
export function useBrokersModifyBrokerDialog(): {
  show: (
    brokerToModify: SavedBroker,
  ) => Promise<BrokersModifyResult | undefined>;
  dialogs: ReactNode;
} {
  const [open, setOpen] = useState(false);
  const [openCount, setOpenCount] = useState(0);
  const [brokerToModify, setBrokerToModify] = useState<SavedBroker | undefined>(
    undefined,
  );
  const resolverRef = useRef<
    ((result: BrokersModifyResult | undefined) => void) | undefined
  >(undefined);

  function show(
    savedBroker: SavedBroker,
  ): Promise<BrokersModifyResult | undefined> {
    return new Promise((resolve) => {
      resolverRef.current = resolve;
      setBrokerToModify(savedBroker);
      setOpenCount((count) => count + 1);
      setOpen(true);
    });
  }

  function settle(result: BrokersModifyResult | undefined) {
    resolverRef.current?.(result);
    resolverRef.current = undefined;
  }

  const dialogs = brokerToModify !== undefined && (
    <BrokersModifyDialog
      // Prefixed, not a bare count. A screen that mounts two of these hooks
      // renders their dialogs as siblings, and two siblings keyed `1` are a
      // React key collision — it then reuses one instance for the other
      // dialog, so the form opens holding the other one's state.
      key={`brokers-modify-${openCount}`}
      open={open}
      brokerToModify={brokerToModify}
      onOpenChange={function (nowOpen) {
        setOpen(nowOpen);
        if (!nowOpen) {
          settle(undefined);
        }
      }}
      onFinished={function (result) {
        settle(result);
        setOpen(false);
      }}
    />
  );

  return { show, dialogs };
}
